import { useState, useEffect } from 'react';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import RelatedServices from '../../../components/feature/RelatedServices';
import RelatedIndustries from '../../../components/feature/RelatedIndustries';

export default function MedicalSpasPage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar scrolled={scrolled} />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 lg:px-12 overflow-hidden bg-gradient-to-br from-[#F5F3FF] via-white to-[#FAF5FF]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-[#7C3AED]/20">
                <div className="w-2 h-2 bg-[#7C3AED] rounded-full animate-pulse"></div>
                <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Medical Spa Marketing</span>
              </div>

              <h1 className="text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight">
                <span className="text-gray-900">Marketing for</span>
                <br />
                <span className="bg-gradient-to-r from-[#7C3AED] to-[#A855F7] bg-clip-text text-transparent">Medical Spas</span>
                <br />
                <span className="text-gray-900">& Aesthetic Clinics</span>
              </h1>

              <p className="text-lg text-gray-600 leading-relaxed">
                Specialized digital marketing for medical spas and aesthetic practices. We help you attract clients seeking cosmetic treatments, body contouring, and aesthetic enhancements through visually compelling, HIPAA-compliant campaigns that drive bookings.
              </p>

              <div className="flex flex-wrap gap-4">
                <a
                  href="/book-strategy-call"
                  className="bg-gradient-to-r from-[#7C3AED] to-[#A855F7] text-white px-8 py-4 rounded-lg hover:from-[#6D28D9] hover:to-[#9333EA] transition-all hover:shadow-lg cursor-pointer whitespace-nowrap inline-flex items-center gap-2 group font-medium"
                >
                  <span>Book Strategy Call</span>
                  <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                </a>
                <a
                  href="/automation-platform"
                  className="border-2 border-[#7C3AED] text-[#7C3AED] px-8 py-4 rounded-lg hover:bg-[#7C3AED] hover:text-white transition-all cursor-pointer whitespace-nowrap font-medium"
                >
                  See Our Platform
                </a>
              </div>
            </div>

            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://public.readdy.ai/ai/img_res/eb7d65f2880b23316af4cdcd35984a90.jpg"
                  alt="Medical Spa"
                  className="w-full h-auto object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Challenges Section */}
      <section className="py-20 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
              UNIQUE CHALLENGES
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 leading-tight mb-6">
              Marketing Challenges for Medical Spas
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Medical spas face intense competition and need to balance aesthetic appeal with medical credibility while navigating advertising restrictions.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-[#F9FAFB] p-8 rounded-xl border border-gray-100">
              <div className="w-12 h-12 bg-[#7C3AED]/10 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-contrast-2-line text-[#7C3AED] text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Intense Local Competition</h3>
              <p className="text-gray-600 leading-relaxed">
                Standing out in saturated markets requires strategic positioning, compelling visuals, and differentiated service offerings.
              </p>
            </div>

            <div className="bg-[#F9FAFB] p-8 rounded-xl border border-gray-100">
              <div className="w-12 h-12 bg-[#7C3AED]/10 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-image-line text-[#7C3AED] text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Visual Marketing Restrictions</h3>
              <p className="text-gray-600 leading-relaxed">
                Navigating before/after photo regulations and social media advertising policies for cosmetic procedures.
              </p>
            </div>

            <div className="bg-[#F9FAFB] p-8 rounded-xl border border-gray-100">
              <div className="w-12 h-12 bg-[#7C3AED]/10 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-calendar-check-line text-[#7C3AED] text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Appointment Optimization</h3>
              <p className="text-gray-600 leading-relaxed">
                Maximizing booking rates and reducing no-shows while managing multiple treatment types and provider schedules.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="py-20 px-6 lg:px-12 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://readdy.ai/api/search-image?query=medical%20spa%20aesthetic%20treatment%20consultation%20professional%20injector%20cosmetic%20procedure%20modern%20clinic%20luxury%20beauty%20wellness%20setting%20patient%20care%20elegant%20environment&width=700&height=600&seq=medspa-solution1&orientation=landscape"
                  alt="Medical Spa Solutions"
                  className="w-full h-auto object-cover object-top"
                />
              </div>
            </div>

            <div className="space-y-8">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
                  OUR SOLUTIONS
                </div>
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 leading-tight mb-6">
                  Specialized Marketing for Medical Spas
                </h2>
              </div>

              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-instagram-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Visual Social Media Marketing</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Instagram and TikTok strategies that showcase your treatments, results, and expertise while staying compliant with advertising regulations.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-search-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Local SEO Domination</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Optimize for treatment-specific searches like "Botox near me" and "laser hair removal [city]" to capture high-intent local traffic.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-advertisement-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Treatment-Specific PPC Campaigns</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Targeted Google and social ads for specific treatments with optimized landing pages that convert browsers into bookings.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-robot-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Automated Booking & Follow-up</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Smart automation for appointment reminders, post-treatment follow-ups, and membership renewals to maximize retention and revenue.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-20 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
              PROVEN RESULTS
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 leading-tight">
              Real Growth for Medical Spas
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-8 bg-[#F9FAFB] rounded-xl">
              <div className="text-5xl font-bold bg-gradient-to-r from-[#7C3AED] to-[#A855F7] bg-clip-text text-transparent mb-3">
                +380%
              </div>
              <div className="text-gray-900 font-bold mb-2">Online Bookings</div>
              <div className="text-sm text-gray-600">Within 4 months of launch</div>
            </div>

            <div className="text-center p-8 bg-[#F9FAFB] rounded-xl">
              <div className="text-5xl font-bold bg-gradient-to-r from-[#7C3AED] to-[#A855F7] bg-clip-text text-transparent mb-3">
                92%
              </div>
              <div className="text-gray-900 font-bold mb-2">Appointment Show Rate</div>
              <div className="text-sm text-gray-600">With automated reminders</div>
            </div>

            <div className="text-center p-8 bg-[#F9FAFB] rounded-xl">
              <div className="text-5xl font-bold bg-gradient-to-r from-[#7C3AED] to-[#A855F7] bg-clip-text text-transparent mb-3">
                5.2x
              </div>
              <div className="text-gray-900 font-bold mb-2">ROI on Marketing</div>
              <div className="text-sm text-gray-600">Average across med spa clients</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 lg:px-12 bg-gradient-to-br from-[#0D9488] to-[#14B8A6]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
            Ready to Grow Your Medical Spa?
          </h2>
          <p className="text-xl text-white/90 mb-8 leading-relaxed">
            Let's discuss how our specialized marketing strategies can help you attract more clients and fill your appointment calendar.
          </p>
          <a
            href="/book-strategy-call"
            className="bg-white text-[#0D9488] px-10 py-5 rounded-lg hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap inline-flex items-center gap-2 group font-bold text-lg shadow-xl"
          >
            <span>Schedule Your Free Strategy Call</span>
            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
          </a>
        </div>
      </section>

      <RelatedServices currentSlug="medical-spas" />
      <RelatedIndustries currentSlug="medical-spas" />
      <Footer />
    </div>
  );
}
